import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  DatePicker,
  Radio,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import { fetchData, generateReport } from "../../../features/fetch/fetchSlice";
import dayjs from "dayjs";
import ProgressToDateTable from "./ProgressToDateTable";
import { useWatch } from "antd/es/form/Form";
import Images from "../progress/Images";

const { TextArea } = Input;
const MonthlyAdd = () => {
  const urlProjects = "main_project/fetch_mainprojects";
  const urlComponent = "project_component/fetch_projectcomponents";
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { activeObjMontProj, dataProjects, dataComponent } = useSelector(
    (state) => ({
      activeObjMontProj: state.obj.activeObj["monthlyProj"],
      dataProjects: state.fetch.data[urlProjects] || [],
      dataComponent: state.fetch.data[urlComponent] || [],
    }),
  );

  const { counties } = useSelector((state) => state.fetch);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  async function handleCancel() {
    await navigate("/rob/monthly-report");
    await setDownload(false);
  }

  const [download, setDownload] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const handleDownload = async (item) => { 
    setReportLoading(true);
    const res = await dispatch(
      generateReport({
        url: "api/v1/reports/directReport",
        reportName: "PROJECT_UPDATE_REPORT",
        itemId: item,
        reportFormat: "PDF",
      }),
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;

      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];

      let fileName = "report";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      setReportLoading(false);

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      await toast.success(`${fileName} downloaded successfully`);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error("Failed to download file");
    }
  }; 
  const onFinish = async (data) => {
    data.mrId = activeObjMontProj?.mrId ? activeObjMontProj?.mrId : null;

    data.mrImages = Array.isArray(data?.mrImages)
      ? data?.mrImages
      : data?.mrImages ? [data.mrImages] : [];

    const res = await dispatch(
      save({
        ...data,
        url: "monthly_report/save_monthlyreport",
      }),
    );
    if (res?.payload?.success) {
      if (download) {
        await handleDownload(res?.payload?.result ?? activeObjMontProj?.mrId);
      }
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const plainOptions = ["Yes", "No"];

  const initialFormValues = {
    ...activeObjMontProj,
    mrContractAwardDate: activeObjMontProj?.mrContractAwardDate
      ? dayjs(activeObjMontProj.mrContractAwardDate)
      : null,
    mrAmountPaidAdvanceDate: activeObjMontProj?.mrAmountPaidAdvanceDate
      ? dayjs(activeObjMontProj.mrAmountPaidAdvanceDate)
      : null,
    mrCommencementDate: activeObjMontProj?.mrCommencementDate
      ? dayjs(activeObjMontProj.mrCommencementDate)
      : null,
    mrExpectedCompletionDate: activeObjMontProj?.mrExpectedCompletionDate
      ? dayjs(activeObjMontProj.mrExpectedCompletionDate)
      : null,
    mrEvalDate: activeObjMontProj?.mrEvalDate
      ? dayjs(activeObjMontProj.mrEvalDate)
      : null,
  };

  useEffect(() => {
    form.setFieldsValue(initialFormValues);
  }, [activeObjMontProj, form]);

  const [selectedProjId, setSelectedProjId] = useState();

  const handleProjectNumberChange = (value) => {
    const selectedProject = dataProjects?.find(
      (item) => item?.projectName === value,
    );

    setSelectedProjId(selectedProject?.projectId);
    if (selectedProject) {
      const {
        projectId,
        projectNumber,
        projectCountyId,
        projectContractor,
        projectCost,
        projectTotalDisbursement,
        projectContractAwardDate,
        projectHasAdvancePayment,
        projectAdvancedPayment,
        projectAdvancedPaymentDate,
        projectScope,
        projectStartDate,
        projectEndDate,
        projectCompletionRate,
      } = selectedProject;

      form.setFieldsValue({
        mrProjectId: projectId,
        mrProjectNo: projectNumber || "",
        mrCountyId: projectCountyId || "",
        mrContractorName: projectContractor || "",
        mrContractSum: projectCost || "",
        mrAmountPaid: projectTotalDisbursement || "",

        mrContractAwardDate: projectContractAwardDate
          ? dayjs(projectContractAwardDate)
          : null,
        mrAdvancePaymentAvailable: projectHasAdvancePayment || "",
        mrAmountPaidAdvance: projectAdvancedPayment || "",
        mrAmountPaidAdvanceDate: projectAdvancedPaymentDate
          ? dayjs(projectAdvancedPaymentDate)
          : null,
        mrScope: projectScope || "",
        mrCommencementDate: projectStartDate ? dayjs(projectStartDate) : null,
        mrExpectedCompletionDate: projectEndDate ? dayjs(projectEndDate) : null,
        mrPhysicalProgres: projectCompletionRate || "",
      });
    } else {
      form.resetFields();
    }
  };
  const mrContractSum = Form.useWatch("mrContractSum", form);
  const mrAmountPaid = Form.useWatch("mrAmountPaid", form);

  useEffect(() => {
    if (mrContractSum != null && mrAmountPaid != null) {
      const balance = Math.max(mrContractSum - mrAmountPaid, 0);
      form.setFieldsValue({ mrContractSumBalance: balance });
    } else {
      form.setFieldsValue({ mrContractSumBalance: null });
    }
  }, [mrContractSum, mrAmountPaid]);

  const mrAdvancePaymentAvailable = useWatch("mrAdvancePaymentAvailable", form);

  useEffect(() => {
    if (mrAdvancePaymentAvailable === "No") {
      form.setFieldsValue({
        schContactPerson: null,
        schContactPhone: null,
      });
    }
  }, [mrAdvancePaymentAvailable]);

  const commencementDate = useWatch("mrCommencementDate", form);
  const expectedCompletionDate = useWatch("mrExpectedCompletionDate", form);

  useEffect(() => {
    if (commencementDate) {
      const start = dayjs(commencementDate);
      const today = dayjs();
      const daysElapsed = today.diff(start, "day");
      form.setFieldsValue({
        mrDaysElapsed: `${daysElapsed}`,
      });

      if (expectedCompletionDate) {
        const end = dayjs(expectedCompletionDate);
        const totalDays = end.diff(start, "day");
        const percentage =
          totalDays > 0 ? ((daysElapsed / totalDays) * 100).toFixed(2) : 0;
        form.setFieldsValue({ mrPercentageElapsedDay: percentage });
      } else {
        form.setFieldsValue({ mrPercentageElapsedDay: null });
      }
    } else {
      form.setFieldsValue({
        mrDaysElapsed: null,
        mrPercentageElapsedDay: null,
      });
    }
  }, [commencementDate, expectedCompletionDate]);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Monthly Project Reports", path: "/rob/monthly-report" },
          { title: "Monthly Project Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Monthly Project Report</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title !bg-white !p-0">
              Project Basic Data
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="mrProjectName"
                className=""
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please select project name",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={dataProjects?.map((item, index) => {
                    return {
                      value: item?.projectName,
                      label: item?.projectName,
                    };
                  })}
                  onChange={handleProjectNumberChange}
                  onDropdownVisibleChange={() => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        // projectStatus: "ONGOING",
                        projectStatus: null,
                        start: 0,
                        limit: null,
                      }),
                    );
                  }}
                  showSearch
                  onSearch={(value) => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        start: 0,
                        limit: null,
                        projectName: value || null,
                        // projectStatus: "ONGOING",
                        projectStatus: null,
                      }),
                    );
                  }}
                  optionFilterProp="label"
                  filterOption={false}
                />
              </Form.Item>
              <Form.Item
                name="mrProjectId"
                className="hidden"
                label={"Project Id"}
                rules={[
                  {
                    required: true,
                    message: "Please select project id",
                  },
                ]}
              >
                <Input disabled className="input " />
              </Form.Item>
              <Form.Item
                name="mrProjectNo"
                className=""
                label={"Project Number"}
                rules={[
                  {
                    required: true,
                    message: "Please select project number",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="mrCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  disabled
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </div>

            <div className="custom-title !bg-white !p-0 mt-[1.25rem]">
              Contract Information
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="mrContractorName"
                label={"Contractor Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add contractor name",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
               
              <Form.Item
                name="mrContractSum"
                label="Contract Sum"
                rules={[
                  {
                    required: true,
                    message: "Please add contract sum",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label="Contract Award Date"
                name="mrContractAwardDate"
                rules={[
                  {
                    required: true,
                    message: "Contract award date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label={"Advance Payment  available ? "}
                name="mrAdvancePaymentAvailable"
                rules={[
                  {
                    required: true,
                    message: "Required field",
                  },
                ]}
              >
                <Radio.Group disabled options={plainOptions} />
              </Form.Item>
              {mrAdvancePaymentAvailable === "Yes" && (
                <>
                  <Form.Item
                    name="mrAmountPaidAdvance"
                    label="Amount paid in Advance"
                    rules={[
                      {
                        required: true,
                        message: "Please add amount paid in Advance",
                      },
                    ]}
                  >
                    <InputNumber
                      disabled
                      suffix="Kes."
                      min={0}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                      placeholder="Enter value"
                      className="input flex"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Date of Advance Amount Paid"
                    name="mrAmountPaidAdvanceDate"
                    rules={[
                      {
                        required: true,
                        message: "Date of advance amount paid is required",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled
                      format={"YYYY MMM DD"}
                      className="input"
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                name="mrAmountPaid"
                label="Amount Paid to Date"
                rules={[
                  {
                    required: false,
                    message: "Please add amount paid to date",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="mrRaisedIpcs"
                label="Raised IPCs"
                rules={[
                  {
                    required: true,
                    message: "Please add raised IPCs",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="mrPendingIpcs"
                label="IPCs Pending Payment"
                rules={[
                  {
                    required: true,
                    message: "Please add IPCs Pending Payment",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="mrContractSumBalance"
                label="Balance of the Contract Sum"
                rules={[
                  {
                    required: true,
                    message: "Please add balance of the contract sum",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="mrScope"
                label={"Scope"}
                rules={[
                  {
                    required: true,
                    message: "Please add scope",
                  },
                ]}
              >
                <TextArea disabled className="text_area" />
              </Form.Item>
            </div>

            <div className="custom-title !bg-white !p-0 mt-[1.25rem]">
              Project Progress
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                label="Inspection Date"
                name="mrEvalDate"
                rules={[
                  {
                    required: true,
                    message: "inspection date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>

              <Form.Item
                label="Commencement Date"
                name="mrCommencementDate"
                rules={[
                  {
                    required: true,
                    message: "Commencement date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Expected Date of Completion"
                name="mrExpectedCompletionDate"
                rules={[
                  {
                    required: true,
                    message: "Expected date of completion is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
            </div>
            <div className="grid lg:grid-cols-3 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="mrDaysElapsed"
                label={"Days elapsed"}
                rules={[
                  {
                    required: true,
                    message: "Please add days elapsed",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="mrPercentageElapsedDay"
                label={"% Days Lapsed"}
                rules={[
                  {
                    required: true,
                    message: "Please add % Days lapsed",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="mrPhysicalProgres"
                label={"Physical Progress"}
                rules={[
                  {
                    required: true,
                    message: "Please add physical progress",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  max="100"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
            </div>

            {selectedProjId && (
              <>
                {" "}
                <div className="custom-title !bg-white !p-0 mt-[1.25rem]">
                  Progress to date
                </div>{" "}
                <ProgressToDateTable selectedProjId={selectedProjId} />
              </>
            )}

            <div className="custom-title !bg-white !p-0 mt-[1.25rem]">
              Challenges
            </div>
            <Form.Item
              name="mrChallenges"
              label={"Describe"}
              rules={[
                {
                  required: true,
                  message: "Please add describe",
                },
              ]}
            >
              <TextArea className="text_area" />
            </Form.Item>

            <div className="custom-title !bg-white !p-0 mt-[1.25rem]">Uploaded Images</div>
            <Form.Item name="mrImages" hidden>
              <Select mode="multiple" />
            </Form.Item>
            <Images form={form} existingImages={activeObjMontProj?.mrImages} fieldName="mrImages" />

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>
                <div className="w-[200px]">
                  <button
                    disabled={saving || reportLoading}
                    type="submit"
                    onClick={() => setDownload(true)}
                    className="cstm-btn"
                  >
                    {(saving || reportLoading) && download ? (
                      <Spin className="blu_spin" />
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>
                <div className="w-[150px]">
                  <button
                    disabled={saving || reportLoading}
                    key="submit"
                    type="submit"
                    className="cstm-btn-2"
                    onClick={() => setDownload(false)}
                  >
                    {(saving || reportLoading) && !download ? (
                      <Spin />
                    ) : activeObjMontProj?.mrId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default MonthlyAdd;
